import { Input } from '@glu/form-components';
import { createUseStyles } from '@glu/theming';
import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import locale from '@glu/locale';
import classnames from 'classnames';
import debounce from './utils/debounce';
import styles from './FilterInput.styles';
import { filterOptionsByString } from './utils/formatOptions';
import { DropdownActions, DropdownProps } from './Dropdown';
import { PanelState } from './PanelContext';
import { State } from './DropdownContext';
import { PanelActions } from './Panel';

const useStyles = createUseStyles(styles);

interface Props {
  applySelection: DropdownActions['applySelection'];
  dark?: boolean;
  filterBy: DropdownProps['filterBy'];
  filterDebounce?: DropdownProps['filterDebounce'];
  filterHtmlValue: PanelState['filterHtmlValue'];
  filterPlaceholder?: DropdownProps['filterPlaceholder'];
  filterString: PanelState['filterString'];
  isFocused?: boolean;
  matchBy: DropdownProps['matchBy'];
  multiselect?: DropdownProps['multiselect'];
  onFilter?: DropdownProps['onFilter'];
  options: DropdownProps['options'];
  selection: State['selection'];
  setArrowDirection: PanelActions['setArrowDirection'];
  setFilterHtmlValue: PanelActions['setFilterHtmlValue'];
  setFilterString: PanelActions['setFilterString'];
  setFocusedIndex: DropdownActions['setFocusedIndex'];
  setIsFilterFocused: DropdownActions['setIsFilterFocused'];
  setSelection: DropdownActions['setSelection'];
}

const FilterInput = ({
  applySelection,
  dark,
  filterBy,
  filterDebounce = undefined,
  filterHtmlValue,
  filterPlaceholder = undefined,
  filterString,
  isFocused,
  matchBy,
  multiselect,
  onFilter,
  options,
  selection,
  setArrowDirection,
  setFilterHtmlValue,
  setFilterString,
  setFocusedIndex,
  setIsFilterFocused,
  setSelection,
}: Props) => {
  const classes = useStyles();
  const debouncer = useMemo(() => debounce((filter) => filter(), 500), []);

  const inputRef = useRef(null);

  useEffect(() => {
    if (isFocused) {
      inputRef.current.querySelector('input').focus();
      setIsFilterFocused(false);
    }
  }, [isFocused, setIsFilterFocused]);

  const filteredOptions = filterOptionsByString(options, filterString, filterBy, matchBy);

  const handleChange = useCallback(
    (event) => {
      const { value } = event.target;
      setFilterHtmlValue(value);

      if (filterDebounce) {
        filterDebounce(() => {
          onFilter(value);
          setFilterString(value);
        });
      } else {
        debouncer(() => {
          onFilter(value);
          setFilterString(value);
        });
      }
    },
    [debouncer, filterDebounce, onFilter, setFilterHtmlValue, setFilterString]
  );

  const handleKeyDown = useCallback(
    ({ key }: React.KeyboardEvent) => {
      switch (key) {
        case 'ArrowDown':
        case 'Tab':
          setArrowDirection('down');
          setFocusedIndex(1);
          break;
        case 'Enter':
          if (filteredOptions.length === 0) {
            applySelection(selection);
          } else if (multiselect) {
            if (
              !selection.some((selectedOption) => selectedOption.id === filteredOptions[0].id) &&
              filterHtmlValue.length > 0
            ) {
              setSelection([...selection, filteredOptions[0]]);
            }
          } else {
            applySelection([filteredOptions.find(({ disabled }) => !disabled)]);
          }
          break;
        default:
      }
    },
    [
      applySelection,
      filterHtmlValue.length,
      filteredOptions,
      multiselect,
      selection,
      setArrowDirection,
      setFocusedIndex,
      setSelection,
    ]
  );

  // Cannot add htmlId to this -- requires an update to form-components,
  // otherwise there will be duplicate IDs for a dropdown and input
  return (
    <div ref={inputRef} className={classes.root}>
      <Input
        autoComplete="off"
        className={classnames(classes.inputWrapper, { [classes.dark]: dark })}
        dark={dark}
        data-qa="filter-text-input"
        disableInputAPI
        labelText={filterPlaceholder || locale.get('filter')}
        name="filter"
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={filterString ? '' : filterPlaceholder || locale.get('filter')}
        role="searchbox"
        screenReaderLabel
        type="text"
        value={filterHtmlValue}
      />
      {filterBy === 'startsWith' && filterString && filteredOptions.length > 0 && (
        <div
          className={classnames(classes.autocomplete, { [classes.dark]: dark })}
          data-qa="filter-autocomplete"
        >
          <span className={classes.spacer} data-qa="input-spacer">
            {filterString}
          </span>
          <span
            className={classnames(classes.suggestion, { [classes.dark]: dark })}
            data-qa="input-suggestion"
          >
            {filteredOptions[0].name.substr(filterString.length)}
          </span>
        </div>
      )}
    </div>
  );
};

export default FilterInput;
